import { format as formatDate } from "date-fns";
import type { Immutable } from "immer";
import React from "react";
import { getReactivatedContext } from "tsi-common-react/src/utils/environment";
import { msgid, ngettext, t } from "ttag";

import { Context, type templates } from "@reactivated";

import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import shipping from "../../utils/shipping";
import { OrderConfirmationFooter } from "../OrderConfirmationFooter";
import { OrderConfirmationHeader } from "../OrderConfirmationHeader";

import styles from "./index.module.scss";

// The shared template types `order` as nullable (the sibling `order_detail`
// prop carries the pillows shape), but this component is only rendered when the
// wrapper has guarded on a populated cocoon order, so strip the null here.
type IOrderProps = NonNullable<
    Immutable<templates.ThankYouDocumentTemplate["order"]>
>;
type ILineProps = IOrderProps["lines"][number];

const useStaticURL = (): string => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const context = React.useContext(Context) || getReactivatedContext();
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    return context?.STATIC_URL ?? "/static/";
};

// Sum line quantities for a shipping-method type ("small-package" / "freight"),
// matching the old l10n DOM sweep that counted `.shpping-method--<type>` nodes
// by their data-quantity.
const sumQtyByType = (lines: IOrderProps["lines"], type: string): number =>
    lines
        .filter((line) => line.shipping_method_type === type)
        .reduce((total, line) => total + line.quantity, 0);

const pluralizeItems = (count: number): string =>
    ngettext(msgid`${count} item`, `${count} items`, count);

// Per-line estimated arrival date. Replaces the l10n-fill-shipment-arrival-date
// DOM sweep: the arrival date is fetched (per the line's destination + estimated
// ship date) and rendered from React state once the TNT API resolves.
const ShipmentArrivalDate = (
    props: Immutable<{
        format: string;
        destinationPostcode: string;
        shippedOnIso: string;
    }>,
): React.JSX.Element => {
    const { format, destinationPostcode, shippedOnIso } = props;
    const [arrivalDate, setArrivalDate] = React.useState<Date | undefined>();
    React.useEffect(() => {
        if (!destinationPostcode) {
            return;
        }
        let mounted = true;
        const shippedOn = formatDate(new Date(shippedOnIso), "yyyy-MM-dd");
        void shipping
            .getShipmentArrivalDate(destinationPostcode, shippedOn)
            .then((response) => {
                if (mounted && response?.arrives_on) {
                    setArrivalDate(response.arrives_on);
                }
            });
        return () => {
            mounted = false;
        };
    }, [destinationPostcode, shippedOnIso]);
    return (
        <span
            className="l10n-fill-shipment-arrival-date"
            data-format={format}
            data-destination={destinationPostcode}
            data-shipped-on={shippedOnIso}
        >
            {arrivalDate ? formatDate(arrivalDate, format) : ""}
        </span>
    );
};

/**
 * Per-line block: figure, title, size, quantity, shipping method, arrival, and
 * per-line pricing. The estimated arrival is rendered by ShipmentArrivalDate,
 * which fetches the TNT date from React state.
 */
const OrderLine = (
    props: Immutable<{ line: ILineProps; inHomeDeliveryURL: string }>,
): React.JSX.Element => {
    const { line, inHomeDeliveryURL } = props;
    const shippingMethodStrong = (
        <strong
            className={`shpping-method--${line.shipping_method_type}`}
            data-quantity={line.quantity}
        >
            {line.shipping_method_label}
        </strong>
    );
    return (
        <div>
            <figure>
                {line.image_url && (
                    <img
                        src={line.image_url}
                        alt={line.image_alt}
                        loading="lazy"
                        decoding="async"
                    />
                )}
            </figure>
            <div>
                <h4>{line.title}</h4>
                {line.option_size && (
                    <p>
                        {t`Size:`} {line.option_size}
                    </p>
                )}
                <p>
                    {t`Qty:`} {line.quantity}
                </p>
                {line.is_bundled && (
                    <p className="order-detail__bundle-callout">
                        {line.bundle_callout_text}
                    </p>
                )}
                <p>
                    {t`Shipping Method:`} <br className={styles.lineBreak} />
                    {line.is_freight ? (
                        <a href={inHomeDeliveryURL}>{shippingMethodStrong}</a>
                    ) : (
                        shippingMethodStrong
                    )}
                </p>
                {line.shipment_number && (
                    <p>
                        {t`Shipping Tracking Number:`} {line.shipment_number}
                    </p>
                )}
                <p>
                    <em>
                        {line.is_freight ? (
                            t`We will contact you to setup a delivery time that works best for you.`
                        ) : (
                            <>
                                {t`Delivered as soon as`}{" "}
                                <ShipmentArrivalDate
                                    format={line.arrival.format}
                                    destinationPostcode={
                                        line.arrival.destination_postcode
                                    }
                                    shippedOnIso={line.arrival.shipped_on_iso}
                                />
                            </>
                        )}
                    </em>
                </p>
            </div>
            <div>
                {!line.is_bundled && line.price && (
                    <>
                        {line.price.is_discounted ? (
                            <>
                                <p
                                    className={concatClassNames([
                                        styles.orderLinePrice,
                                        styles.orderLinePriceStrike,
                                    ])}
                                >
                                    <span className="ada-screenreader-only">
                                        {t`Original Price:`}
                                    </span>
                                    {line.price.msrp_display}
                                </p>
                                <p
                                    className={concatClassNames([
                                        styles.orderLinePrice,
                                        styles.orderLinePriceHighlight,
                                    ])}
                                >
                                    <span className="ada-screenreader-only">
                                        {t`Current Price:`}
                                    </span>
                                    {line.price.actual_display}
                                </p>
                            </>
                        ) : (
                            <p className={styles.orderLinePrice}>
                                {line.price.actual_display}
                            </p>
                        )}
                        {line.price.addon_display && (
                            <p className={styles.orderLinePrice}>
                                +{line.price.addon_display}
                            </p>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

/**
 * Payment + order-summary + totals (addresses, payment method, grouped lines,
 * and the totals breakdown). The order-summary count is derived from the order
 * lines and rendered directly into its holder span.
 */
const OrderDetailsBody = (
    props: Immutable<{ order: IOrderProps }>,
): React.JSX.Element => {
    const { order } = props;
    return (
        <>
            <div className={styles.orderConfirmationPayment}>
                <div>
                    <h4>{t`Shipping Address`}</h4>
                    <p>{order.shipping_address.name}</p>
                    <p>{order.shipping_address.line1}</p>
                    <p>{order.shipping_address.line2}</p>
                    <p>
                        {order.shipping_address.city},{" "}
                        {order.shipping_address.state}{" "}
                        {order.shipping_address.postcode}
                    </p>
                    <p>{order.shipping_address.country}</p>
                    <p>{order.shipping_address.phone_number}</p>
                </div>

                <div>
                    <h4>{t`Billing Address`}</h4>
                    <p>{order.billing_address.line1}</p>
                    <p>{order.billing_address.line2}</p>
                    <p>
                        {order.billing_address.city},{" "}
                        {order.billing_address.state}{" "}
                        {order.billing_address.postcode}
                    </p>
                    <p>{order.billing_address.country}</p>
                    <p>{order.billing_address.phone_number}</p>
                    <p>{order.email}</p>
                </div>

                <div>
                    <h4>{t`Payment Method`}</h4>
                    {order.cybersource_cards.map((card, i) => (
                        <div key={`cs-${i}`}>
                            <p>
                                {card.card_type_name} {t`ending in`}{" "}
                                {card.last4}
                            </p>
                            {card.amount_display && (
                                <p>{card.amount_display}</p>
                            )}
                        </div>
                    ))}
                    {order.financing_transfers.map((transfer, i) => (
                        <div key={`fin-${i}`}>
                            <p>
                                {t`Account:`} {transfer.masked_account_number}
                            </p>
                            {transfer.amount_display && (
                                <p>{transfer.amount_display}</p>
                            )}
                            {transfer.disclosure && (
                                <p>
                                    {t`Terms:`} {transfer.disclosure}
                                </p>
                            )}
                        </div>
                    ))}
                    <p>{t`Order Total`}</p>
                    <p>{order.order_total_display}</p>
                </div>
            </div>

            <div
                className={concatClassNames([
                    styles.orderConfirmationOrderDetail,
                    "order-confirmation__order-detail",
                ])}
            >
                <p>{t`Orders with two or more items may arrive in separate shipments, with no additional shipping charges.`}</p>
                <div>
                    <h4>
                        {t`Order Summary`} (
                        <span id="order-confirmation-order-summary-holder">
                            {pluralizeItems(
                                sumQtyByType(order.lines, "small-package") +
                                    sumQtyByType(order.lines, "freight"),
                            )}
                        </span>
                        )
                    </h4>
                    {order.lines.map((line, i) => (
                        <OrderLine
                            key={i}
                            line={line}
                            inHomeDeliveryURL={order.in_home_delivery_url}
                        />
                    ))}
                    <div>
                        <dl>
                            <dt>{t`Subtotal:`}</dt>
                            <dd>{order.subtotal_display}</dd>
                        </dl>
                        {order.has_discount && (
                            <dl>
                                <dt>{t`Discount:`}</dt>
                                <dd>{order.discount_display}</dd>
                            </dl>
                        )}
                        <dl>
                            <dt>{t`Shipping:`}</dt>
                            <dd>{order.shipping_display}</dd>
                        </dl>
                        {order.is_tax_known ? (
                            order.itemized_tax_groups.map((group, i) => (
                                <dl key={i}>
                                    <dt>{group.name}</dt>
                                    <dd>{group.amount_display}</dd>
                                </dl>
                            ))
                        ) : (
                            <>
                                <dl>
                                    <dt>{t`Tax:`}</dt>
                                    <dd>---</dd>
                                </dl>
                                <dl>
                                    {/* The legacy template emitted colspan="2"
                                        on this <dd>; colspan is not a valid
                                        attribute on <dd> (browsers ignore it),
                                        so it is omitted -- no visual change. */}
                                    <dd>
                                        <em>{t`Tax will be calculated upon shipment`}</em>
                                    </dd>
                                </dl>
                            </>
                        )}
                        <dl>
                            <dt>{t`Total:`}</dt>
                            <dd>{order.total_display}</dd>
                        </dl>
                    </div>
                </div>
            </div>
        </>
    );
};

/**
 * The full order-confirmation section: header, shipping summary, order-number
 * block, payment/order-summary/totals, financing e-signature, and the
 * customer-service footer.
 *
 * Reused by the anonymous order-detail page; keep presentation here and feed
 * order data + URLs/contact via the typed OrderDetailProps.
 */
export const OrderDetail = (
    props: Immutable<{ order: IOrderProps }>,
): React.JSX.Element => {
    const { order } = props;
    const staticURL = useStaticURL();
    const esigDate = order.esig_signed_date;
    const esigTime = order.esig_signed_time;
    return (
        <section
            id="order-detail"
            className="order-confirmation l-capped-width"
        >
            <div
                id="thanks"
                data-qty={order.num_items}
                data-revenue={order.total_incl_tax}
                data-number={order.order_number}
                data-continue-url={order.continue_url}
            >
                <OrderConfirmationHeader>
                    <h1>{t`Thank you for choosing Cocoon!`}</h1>
                    <p>
                        {t`Hi,`} {order.customer_first_name}!{" "}
                        {t`We've received your order and will get started on it right away.`}
                    </p>
                    <p>{t`You will be receiving an order confirmation email soon`}</p>
                    <a
                        onClick={(e) => {
                            e.preventDefault();
                            getWindow()?.print();
                        }}
                        href="#"
                    >
                        <img
                            src={`${staticURL}img/order/print@2x.png`}
                            alt=""
                            loading="lazy"
                            decoding="async"
                        />
                        {t`Print`}
                    </a>
                </OrderConfirmationHeader>

                <div className={styles.orderConfirmationShipping}>
                    <figure>
                        <img
                            src={`${staticURL}img/order/delivery@2x.png`}
                            alt=""
                            loading="lazy"
                            decoding="async"
                        />
                    </figure>
                    <div>
                        {order.has_ups && (
                            <p>
                                {t`You have`}{" "}
                                <span id="order-confirmation-shipping-method-small-package-holder">
                                    {pluralizeItems(
                                        sumQtyByType(
                                            order.lines,
                                            "small-package",
                                        ),
                                    )}
                                </span>{" "}
                                {t`being shipped via UPS. You will receive an email with tracking information soon.`}
                            </p>
                        )}
                        {order.has_wgd && (
                            <p>
                                {t`You have`}{" "}
                                <span id="order-confirmation-shipping-method-freight-holder">
                                    {pluralizeItems(
                                        sumQtyByType(order.lines, "freight"),
                                    )}
                                </span>{" "}
                                {t`being shipped via`}{" "}
                                <a href={order.in_home_delivery_url}>
                                    {t`White Glove Delivery`}
                                </a>
                                .{" "}
                                {t`We will contact you soon to setup a delivery time that works best for you.`}
                            </p>
                        )}
                    </div>
                </div>

                <div className={styles.orderConfirmationOrderNumber}>
                    <div>
                        <p>
                            <span>{t`Order Number:`}</span> {order.order_number}
                        </p>
                        <p>
                            {t`Placed on`} {order.date_placed_display}
                        </p>
                    </div>
                    <div>
                        <p>
                            {t`You can check your order status anytime at`}{" "}
                            <a href={order.order_lookup_url}>
                                <span>cocoonbysealy.com/orders.</span>
                            </a>{" "}
                            {t`Please remember to have your order number ready when checking.`}
                        </p>
                    </div>
                </div>

                <OrderDetailsBody order={order} />

                {order.has_financing && (
                    <div className={styles.thankYouEsig}>
                        <span>{t`Electronic Signature:`}</span>{" "}
                        {t`Acknowledged & Signed on ${esigDate} ${esigTime}`}
                    </div>
                )}
            </div>

            <OrderConfirmationFooter>
                <p>
                    {t`Any questions or concerns? You can contact us by calling toll-free`}{" "}
                    <strong>
                        <a href={order.cs_phone_rfc3966}>
                            {order.cs_phone_national}
                        </a>
                    </strong>{" "}
                    {t`or by sending an email to`}{" "}
                    <strong>
                        <a href={`mailto:${order.cs_email}`}>
                            {order.cs_email}
                        </a>
                    </strong>
                    .
                </p>
                <br />
                <p>Cocoon by Sealy, 1000 Tempur Way, Lexington, KY 40511</p>
            </OrderConfirmationFooter>
        </section>
    );
};
